import { Tabs } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { User } from '@/api/User';
import { useAuthContext } from '@/auth/AuthContext';
import Content from '@/components/Content';
import { OrgUtil } from '@/data/OrgUtil';
import { userService } from '@/service/UserService';
import { AbsenceCalendar } from '../timesheet/OverviewAbsence';
import TimesheetCalendar from '../timesheet/components/TimesheetCalendar';
import UserSetup from './UserSetup';

const DirectReportDetails = () => {
    const { id } = useParams();
    const reportId = Number(id);
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedTab = searchParams.get('tab');
    const activeTab = requestedTab === 'absences' || requestedTab === 'setup' ? requestedTab : 'timesheets';
    const initialMonth = useMemo(() => {
        const requestedMonth = searchParams.get('month');
        if (!requestedMonth || !/^\d{4}-(0[1-9]|1[0-2])$/.test(requestedMonth)) return undefined;
        const [year, month] = requestedMonth.split('-').map(Number);
        if (year < 1900 || year > 2500) return undefined;
        return new Date(year, month - 1, 1);
    }, [searchParams]);
    const { user } = useAuthContext();
    const [report, setReport] = useState<User | null>(null);
    const role = user?.data?.role;
    const canEditReporting = role ? OrgUtil.isRoleAdmin(role) || OrgUtil.isRoleRoot(role) : false;

    useEffect(() => {
        if (!Number.isFinite(reportId)) return;
        userService.find(reportId)
            .then(setReport)
            .catch(error => {
                console.error('Failed to load direct report', error);
                setReport(null);
            });
    }, [reportId]);

    const reportName = report ? `${report.firstName} ${report.lastName}` : 'Direkter Bericht';

    return (
        <Content title={reportName} description='Genehmigungen und Einstellungen'>
            <Tabs.Root value={activeTab} onValueChange={({ value }) => {
                const nextSearchParams = new URLSearchParams(searchParams);
                nextSearchParams.set('tab', value);
                setSearchParams(nextSearchParams, { replace: true });
            }} lazyMount>
                <Tabs.List>
                    <Tabs.Trigger value='timesheets'>
                        Zeiterfassung
                    </Tabs.Trigger>
                    <Tabs.Trigger value='absences'>
                        Abwesenheiten
                    </Tabs.Trigger>
                    <Tabs.Trigger value='setup'>
                        Einstellungen
                    </Tabs.Trigger>
                </Tabs.List>
                <Tabs.Content value='timesheets' px='0' pt='6' pb='0' minW='0'>
                    <TimesheetCalendar userId={reportId} organizationId={user?.data?.organizationId} initialMonth={initialMonth} active={activeTab === 'timesheets'} />
                </Tabs.Content>
                <Tabs.Content value='absences' px='0' pt='6' pb='0' minW='0'>
                    <AbsenceCalendar userId={reportId} initialMonth={initialMonth} active={activeTab === 'absences'} />
                </Tabs.Content>
                <Tabs.Content value='setup' px='0' pt='6' pb='0' minW='0'>
                    <UserSetup
                        userId={reportId}
                        canEditProfile
                        canEditSetup
                        canEditReporting={canEditReporting}
                    />
                </Tabs.Content>
            </Tabs.Root>
        </Content>
    );
};

export default DirectReportDetails;
